'use client';
import React from 'react';
import Link from 'next/link';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface MasjidAccessibilityProps {
  breadcrumbs?: BreadcrumbItem[];
  pageId?: string;
}

export default function MasjidAccessibility({ breadcrumbs, pageId = 'main-content' }: MasjidAccessibilityProps) {
  return (
    <>
      <style>{`
        /* ── Skip Links ── */
        .mih-skip-links {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 9999;
          display: flex;
          gap: 4px;
          padding: 4px;
        }
        .mih-skip-link {
          position: absolute;
          left: -9999px;
          top: auto;
          width: 1px;
          height: 1px;
          overflow: hidden;
          background: #1e3a8a;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 600;
          padding: 10px 20px;
          text-decoration: none;
          border: 2px solid rgba(212,175,55,0.8);
          white-space: nowrap;
          transition: none;
        }
        .mih-skip-link:focus {
          position: static;
          width: auto;
          height: auto;
          overflow: visible;
          outline: 3px solid rgba(212,175,55,0.9);
          outline-offset: 2px;
        }

        /* ── Global Focus Indicators ── */
        .mih-media-page *:focus-visible,
        .mih-imam-page *:focus-visible,
        .mih-contact-page *:focus-visible,
        .mih-services-page *:focus-visible,
        .mih-events-page *:focus-visible,
        .mih-announcements-page *:focus-visible,
        .mih-iiua-page *:focus-visible,
        .mih-resource-page *:focus-visible,
        .nm-wrap *:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85) !important;
          outline-offset: 3px !important;
          border-radius: 2px;
        }

        /* ── Breadcrumb Nav ── */
        .mih-a11y-breadcrumb {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 28px;
        }
        .mih-a11y-breadcrumb-link {
          font-size: 12px;
          color: rgba(255,255,255,0.35);
          text-decoration: none;
          transition: color 0.2s;
          padding: 2px 4px;
          border-radius: 2px;
        }
        .mih-a11y-breadcrumb-link:hover {
          color: rgba(255,255,255,0.7);
        }
        .mih-a11y-breadcrumb-link:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85);
          outline-offset: 2px;
          color: rgba(255,255,255,0.8);
        }
        .mih-a11y-breadcrumb-sep {
          font-size: 11px;
          color: rgba(255,255,255,0.18);
          user-select: none;
          aria-hidden: true;
        }
        .mih-a11y-breadcrumb-current {
          font-size: 12px;
          color: rgba(255,255,255,0.28);
          font-style: italic;
        }
      `}</style>

      {/* Skip Navigation Links */}
      <div className="mih-skip-links" role="navigation" aria-label="Skip navigation">
        <a href={`#${pageId}`} className="mih-skip-link">
          Skip to main content
        </a>
        <a href="#mih-footer" className="mih-skip-link">
          Skip to footer
        </a>
      </div>

      {/* Breadcrumb Navigation */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb navigation" className="mih-a11y-breadcrumb">
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span className="mih-a11y-breadcrumb-sep" aria-hidden="true">/</span>
                )}
                {isLast || !crumb.href ? (
                  <span
                    className="mih-a11y-breadcrumb-current"
                    aria-current="page"
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <Link href={crumb.href} className="mih-a11y-breadcrumb-link">
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}
    </>
  );
}
